import { memo, useMemo, type FC } from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Select } from '../../Select/index.tsx';
import type { Option } from '../types.ts';

const getYearsOptions = (min: number, length: number) =>
  Array.from({ length }, (_, index) => ({
    value: min + index,
    label: `${min + index}`,
  }));

interface YearSelectProps {
  date: Date;
  changeYear: (year: number) => void;
  minYear?: number;
  maxYear?: number;
  dataTestId?: string;
}

export const YearSelect: FC<YearSelectProps> = memo(function YearSelect({
  date,
  changeYear,
  minYear = new Date().getFullYear() - 4,
  maxYear = new Date().getFullYear() + 4,
  dataTestId,
}) {
  const yearsOptions = useMemo(() => getYearsOptions(minYear, maxYear - minYear + 1), [minYear, maxYear]);
  const value = useMemo(() => ({ label: `${date.getFullYear()}`, value: date.getFullYear() }), [date]);

  const handleChange = useCallbackRef((option: Option<number> | null) => {
    if (option) {
      changeYear(option.value);
    }
  });

  return (
    <Select<Option<number>>
      value={value}
      onChange={handleChange}
      options={yearsOptions}
      isClearable={false}
      dataTestId={dataTestId ? `${dataTestId}-year-select` : undefined}
    />
  );
});
